import React from "react";
import { ShieldCheck, ScanLine, Layers, FileCheck2, ArrowRight, Building2, Gavel, KeyRound, Home } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const roles = [
    { icon: Building2, label: "Agenzie Immobiliari" },
    { icon: KeyRound, label: "Amministratori di Condominio" },
    { icon: Gavel, label: "Notai & Periti" },
    { icon: Home, label: "Proprietari immobili" },
  ];

  const features = [
    { icon: ScanLine, title: "Acquisizione guidata 1:100", desc: "Scatto o scansione con calibrazione righello e ridimensionamento automatico." },
    { icon: Layers, title: "Sovrapposizione bicolore", desc: "Concessione (rosso) e Catasto (giallo) allineate sullo stesso oggetto." },
    { icon: ShieldCheck, title: "Semaforo di conformità", desc: "Discostamento % con esito Verde <2%, Giallo ≤10%, Rosso oltre." },
    { icon: FileCheck2, title: "Due diligence progressiva", desc: "Servizi specialistici a step per contenere i costi di consulenza." },
  ];

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 relative overflow-hidden">
      <div className="absolute inset-0 blueprint-grid opacity-40" />
      <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-[#FF3366]/10 blur-3xl" />

      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <ShieldCheck className="h-6 w-6 text-slate-900" strokeWidth={2.4} />
            </div>
            <div>
              <p className="font-heading font-extrabold text-lg leading-none tracking-tight">Leggittimità<span className="text-cyan-400">ChekPro</span></p>
              <p className="font-mono-t text-[10px] tracking-widest text-slate-500 uppercase mt-1">Conformità Edilizia</p>
            </div>
          </div>
          <button
            data-testid="google-login-button"
            onClick={handleLogin}
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white text-slate-800 text-sm font-medium hover:bg-slate-100 transition-colors"
          >
            <GoogleIcon /> Accedi con Google
          </button>
        </header>

        <div className="grid lg:grid-cols-2 gap-12 items-center mt-16 lg:mt-24">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/5 mb-6 max-w-full">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse shrink-0" />
              <span className="font-mono-t text-[11px] tracking-wider text-cyan-300 uppercase">Stato Legittimo - Testo Unico Edilizia art. 9-bis DPR 380/2001 e successivi aggiornamenti</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Verifica la <span className="text-cyan-400">conformità edilizia</span> in pochi minuti
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl">
              Sovrapponi la planimetria di concessione edilizia a quella catastale, misura il discostamento
              e ottieni un esito immediato a semaforo. Uno strumento per la prima determinazione dello
              stato legittimo immobiliare.
            </p>

            <div className="flex flex-wrap gap-3 mt-8">
              {roles.map((r) => (
                <div key={r.label} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#121824] border border-slate-800">
                  <r.icon className="h-4 w-4 text-cyan-400" />
                  <span className="text-sm text-slate-300">{r.label}</span>
                </div>
              ))}
            </div>

            <button
              data-testid="google-login-button-hero"
              onClick={handleLogin}
              className="mt-10 group inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-white text-slate-800 font-semibold hover:bg-slate-100 transition-all hover:shadow-xl hover:shadow-cyan-500/10"
            >
              <GoogleIcon /> Accedi con Google
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 fade-up" style={{ animationDelay: "0.15s" }}>
            {features.map((f) => (
              <div key={f.title} className="p-5 rounded-xl bg-[#121824] border border-slate-800 hover:border-cyan-500/40 transition-colors">
                <div className="h-10 w-10 rounded-lg bg-cyan-500/10 flex items-center justify-center mb-4">
                  <f.icon className="h-5 w-5 text-cyan-400" />
                </div>
                <h3 className="font-heading font-semibold text-slate-100">{f.title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);
